import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Edit2, Trash2, StickyNote, BarChart2, Calendar, Filter, X, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import NotesModal from './NotesModal';
import { NOTE_TOPICS, SANS_SUJET, sujetDeNote, couleurDeNote } from '../constants/noteTopics';
import { OPTIONS_PLATEFORMES } from '../constants/plateformes';

const API_URL = config.API_URL;

/**
 * Sujets proposés au filtre.
 *
 * « Sans sujet » n'est pas une valeur du modèle mais une question qu'on pose souvent : c'est
 * ce filtre qui dit ce qu'il reste à reprendre parmi les notes antérieures au champ.
 */
const FILTRES_SUJET = [
  { value: '', label: 'Tous les sujets' },
  ...NOTE_TOPICS.map(t => ({ value: t.value, label: t.label })),
  { value: SANS_SUJET, label: 'Sans sujet' }
];

// Couleurs prédéfinies
const COLOR_LABELS = {
  '#f59e0b': 'Orange',
  '#ef4444': 'Rouge',
  '#22c55e': 'Vert',
  '#1a1a1a': 'Bleu',
  '#8b5cf6': 'Violet',
  '#ec4899': 'Rose'
};

// Plateformes
// Trois régies seulement figuraient ici : tout le reste — Pinterest, TikTok, ChatGPT Ads et
// les solutions de mesure — s'affichait en clé brute et ne se filtrait pas.
const PLATFORMS = [{ value: '', label: 'Toutes les plateformes' }, ...OPTIONS_PLATEFORMES];

// Formater une date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Formater la portée de la note
const formatScope = (note) => {
  const parts = [];

  if (note.clientName) {
    parts.push(note.clientName);
  }
  if (note.siteName) {
    parts.push(note.siteName);
  }
  if (note.platform) {
    const platformLabel = PLATFORMS.find(p => p.value === note.platform)?.label || note.platform;
    parts.push(platformLabel);
  }
  if (note.accountId) {
    parts.push(`Compte: ${note.accountId}`);
  }

  return parts.length > 0 ? parts.join(' > ') : 'Tous';
};

/**
 * Les colonnes du tableau d'archive, et la clé de tri que chacune demande au serveur.
 *
 * `cle` à `null` signale une colonne qui ne se trie pas — « Actions » ne contient que des
 * boutons.
 *
 * `sensInitial` est la direction du premier clic, et il n'est pas le même partout : sur une
 * date ou une case cochée, on cherche d'abord le plus récent ou ce qui est activé ; sur du
 * texte, on attend A→Z. Un `desc` uniforme aurait ouvert les noms d'auteurs par la fin.
 */
const COLONNES_ARCHIVE = [
  { libelle: 'Date(s)', cle: 'startDate', sensInitial: 'desc', alignement: 'left' },
  { libelle: 'Note', cle: 'content', sensInitial: 'asc', alignement: 'left' },
  { libelle: 'Sujet', cle: 'topic', sensInitial: 'asc', alignement: 'left' },
  { libelle: 'Portée', cle: 'scope', sensInitial: 'asc', alignement: 'left' },
  { libelle: 'Rédacteur', cle: 'author', sensInitial: 'asc', alignement: 'left' },
  { libelle: 'Graphe', cle: 'showOnChart', sensInitial: 'desc', alignement: 'center' },
  { libelle: 'Créée le', cle: 'createdAt', sensInitial: 'desc', alignement: 'left' },
  { libelle: 'Actions', cle: null, alignement: 'right' }
];

const NotesArchive = ({
  token,
  onBack,
  clients = [],
  sites = [],
  accounts = [],
  selectedClientId = null,
  /** Navigation vers les autres écrans de notes, transmise aux onglets de la modale. */
  onOuvrirVue = null
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres - initialiser avec le client sélectionné dans le header
  const [filterClient, setFilterClient] = useState(selectedClientId || '');
  const [filterSite, setFilterSite] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Mettre à jour le filtre client quand selectedClientId change
  useEffect(() => {
    setFilterClient(selectedClientId || '');
    setPage(1); // Réinitialiser la pagination
  }, [selectedClientId]);

  // Pagination
  // Tri du tableau. Le défaut suit la colonne « Date(s) », décroissante : l'archive sert
  // d'abord à retrouver ce qui vient de se passer. Le tri est appliqué par le serveur —
  // la liste étant paginée, trier la page reçue n'aurait trié que vingt lignes.
  const [sortBy, setSortBy] = useState('startDate');
  const [sortOrder, setSortOrder] = useState('desc');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  /**
   * Clic sur un en-tête. Même colonne : on inverse le sens. Colonne différente : on adopte
   * son sens initial plutôt que de conserver celui de la précédente — passer de « Date(s)
   * décroissante » à « Rédacteur » aurait sinon ouvert les auteurs de Z à A.
   *
   * Aucun `setState` n'est appelé depuis la fonction de mise à jour d'un autre : React
   * rejoue ces fonctions en mode strict, et une bascule imbriquée s'y appliquerait deux
   * fois — le sens serait revenu à son point de départ, et le clic paraîtrait sans effet.
   *
   * Le retour à la première page est indispensable : rester page 3 après un changement de
   * tri afficherait les lignes 41 à 60 d'un classement qu'on n'a jamais vu commencer.
   */
  const changerTri = useCallback((cle) => {
    setPage(1);
    if (sortBy === cle) {
      setSortOrder(sens => (sens === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(cle);
      setSortOrder(COLONNES_ARCHIVE.find(c => c.cle === cle)?.sensInitial || 'desc');
    }
  }, [sortBy]);


  // Modal d'édition
  const [editingNote, setEditingNote] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal de suppression
  const [deletingNote, setDeletingNote] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Charger les notes
  const fetchNotes = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (filterClient) params.append('clientId', filterClient);
      if (filterSite) params.append('siteId', filterSite);
      if (filterPlatform) params.append('platform', filterPlatform);
      if (filterTopic) params.append('topic', filterTopic);

      const response = await axios.get(
        `${API_URL}/api/notes/archive?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotes(response.data.notes);
        setTotal(response.data.pagination.total);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError(response.data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Erreur chargement notes:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement des notes');
    } finally {
      setLoading(false);
    }
  }, [token, page, sortBy, sortOrder, filterClient, filterSite, filterPlatform, filterTopic]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  // Reset page quand les filtres changent
  useEffect(() => {
    setPage(1);
  }, [filterClient, filterSite, filterPlatform, filterTopic]);

  // Sites filtrés par client
  const filteredSites = useMemo(() => {
    if (!filterClient) return sites;
    return sites.filter(s => s.clientId === filterClient);
  }, [sites, filterClient]);

  /**
   * La portée que la modale doit reprendre à l'ouverture.
   *
   * Celui qui crée une note depuis l'archive vient de restreindre sa liste — au client du
   * header, repris dans `filterClient`, ou au site choisi dans le panneau de filtres.
   * Ouvrir la modale sur « Tous les clients » lui demandait de refaire à la main la
   * sélection qu'il venait de faire, et une note enregistrée sans client est ensuite
   * introuvable par ce même filtre.
   *
   * ⚠️ Le filtre de plateforme reste dehors, à la différence du client et du site : la
   * modale ne l'affiche nulle part — elle le déduit du compte choisi. Le reprendre poserait
   * sur la note une portée invisible, que son auteur ne pourrait ni voir ni retirer.
   *
   * Seule la création en profite : `editingNote` l'emporte dans la modale, une note
   * existante gardant évidemment la sienne.
   */
  const contexteDeSaisie = useMemo(() => ({
    clientId: filterClient || null,
    siteId: filterSite || null
  }), [filterClient, filterSite]);

  // Gérer la sauvegarde d'une note
  const handleSaveNote = (savedNote) => {
    setNotes(prev => {
      const index = prev.findIndex(n => n.id === savedNote.id);
      if (index >= 0) {
        // Mise à jour
        const updated = [...prev];
        updated[index] = savedNote;
        return updated;
      } else {
        // Nouvelle note
        return [savedNote, ...prev];
      }
    });
    setIsModalOpen(false);
    setEditingNote(null);
  };

  // Supprimer une note
  const handleDelete = async () => {
    if (!deletingNote) return;

    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/notes/${deletingNote.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setNotes(prev => prev.filter(n => n.id !== deletingNote.id));
        setTotal(prev => prev - 1);
        setDeletingNote(null);
      } else {
        setError(response.data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur suppression note:', err);
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Réinitialiser les filtres
  const clearFilters = () => {
    setFilterClient('');
    setFilterSite('');
    setFilterPlatform('');
    setFilterTopic('');
  };

  const hasActiveFilters = filterClient || filterSite || filterPlatform || filterTopic;

  return (
    <div className="step-content">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>
          <StickyNote size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Notes
        </h2>
        <span style={{ fontSize: '14px', color: '#6b7280' }}>
          {total} note{total > 1 ? 's' : ''} au total
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: showFilters || hasActiveFilters ? '#FAF9F5' : 'white',
              border: showFilters || hasActiveFilters ? '1px solid #1a1a1a' : '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: showFilters || hasActiveFilters ? '#1a1a1a' : '#374151'
            }}
          >
            <Filter size={16} />
            Filtres
            {hasActiveFilters && (
              <span style={{
                backgroundColor: '#1a1a1a',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '11px'
              }}>
                {[filterClient, filterSite, filterPlatform, filterTopic].filter(Boolean).length}
              </span>
            )}
          </button>

          <button
            onClick={() => { setEditingNote(null); setIsModalOpen(true); }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              backgroundColor: '#111827',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              color: 'white'
            }}
          >
            + Nouvelle note
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div style={{
          display: 'flex',
          gap: '16px',
          padding: '16px',
          backgroundColor: '#FAF9F5',
          borderRadius: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end'
        }}>
          {clients.length > 0 && (
            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
                Client
              </label>
              <select
                value={filterClient}
                onChange={e => { setFilterClient(e.target.value); setFilterSite(''); }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Tous les clients</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {sites.length > 0 && (
            <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
                Site
              </label>
              <select
                value={filterSite}
                onChange={e => setFilterSite(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Tous les sites</option>
                {filteredSites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
              Plateforme
            </label>
            <select
              value={filterPlatform}
              onChange={e => setFilterPlatform(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {PLATFORMS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div style={{ flex: '1 1 200px', minWidth: '180px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
              Sujet
            </label>
            <select
              value={filterTopic}
              onChange={e => setFilterTopic(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {FILTRES_SUJET.map(t => (
                <option key={t.value || 'tous'} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#6b7280',
                cursor: 'pointer',
                height: '42px'
              }}
            >
              <X size={14} />
              Effacer
            </button>
          )}
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px',
          color: '#6b7280'
        }}>
          <div className="spinner" style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#1a1a1a',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      ) : notes.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 24px',
          backgroundColor: '#FAF9F5',
          borderRadius: '12px'
        }}>
          <Calendar size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#374151' }}>
            Aucune note
          </h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
            {hasActiveFilters
              ? 'Aucune note ne correspond aux filtres'
              : 'Commencez par creer votre premiere note'
            }
          </p>
        </div>
      ) : (
        <>
          {/* Tableau */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  {COLONNES_ARCHIVE.map(colonne => {
                    const triee = colonne.cle && colonne.cle === sortBy;
                    return (
                      <th
                        key={colonne.libelle}
                        onClick={() => colonne.cle && changerTri(colonne.cle)}
                        role={colonne.cle ? 'button' : undefined}
                        tabIndex={colonne.cle ? 0 : undefined}
                        aria-sort={triee ? (sortOrder === 'asc' ? 'ascending' : 'descending') : undefined}
                        onKeyDown={(e) => {
                          if (colonne.cle && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            changerTri(colonne.cle);
                          }
                        }}
                        style={{
                          padding: '14px 16px',
                          textAlign: colonne.alignement,
                          fontSize: '13px',
                          fontWeight: 600,
                          color: triee ? '#111827' : '#6b7280',
                          borderBottom: '1px solid #e5e7eb',
                          cursor: colonne.cle ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {colonne.libelle}
                        {/* La flèche ne s'affiche que sur la colonne triée. Un indicateur sur
                            chaque en-tête ferait passer sept colonnes pour triées à la fois. */}
                        {triee && (
                          <span style={{ marginLeft: '6px', fontSize: '11px' }}>
                            {sortOrder === 'asc' ? '▲' : '▼'}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {notes.map((note, index) => (
                  <tr
                    key={note.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? 'white' : '#FAF9F5',
                      transition: 'background-color 0.15s'
                    }}
                  >
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: couleurDeNote(note),
                            flexShrink: 0
                          }}
                        />
                        <span>
                          {formatDate(note.startDate)}
                          {note.endDate && (
                            <span style={{ color: '#6b7280' }}>
                              {' '}au {formatDate(note.endDate)}
                            </span>
                          )}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: '#111827', borderBottom: '1px solid #f3f4f6' }}>
                      <div
                        title={note.content}
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '280px'
                        }}
                      >
                        {note.content}
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}>
                      {/* Pilule inline, sans classe CSS : les composants Notes n'en emploient
                          aucune, et le style reprend celui des badges tickets et diagnostic.
                          Le libellé accompagne la couleur — elle ne porte rien seule. */}
                      {sujetDeNote(note.topic) ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                          color: sujetDeNote(note.topic).couleur,
                          backgroundColor: sujetDeNote(note.topic).fond
                        }}>
                          {sujetDeNote(note.topic).label}
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
                      {formatScope(note)}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {note.author?.picture ? (
                          <img
                            src={note.author.picture}
                            alt={note.author.name || 'Auteur'}
                            style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: '#e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#6b7280'
                          }}>
                            {(note.author?.name || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '120px'
                        }}>
                          {note.author?.name || 'Inconnu'}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', borderBottom: '1px solid #f3f4f6' }}>
                      {note.showOnChart ? (
                        <BarChart2 size={18} style={{ color: '#22c55e' }} />
                      ) : (
                        <BarChart2 size={18} style={{ color: '#d1d5db' }} />
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', fontSize: '13px', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>
                      {formatDate(note.createdAt)}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right', borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={() => { setEditingNote(note); setIsModalOpen(true); }}
                          title="Modifier"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#FAF9F5',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#1a1a1a'
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeletingNote(note)}
                          title="Supprimer"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            height: '32px',
                            backgroundColor: '#fef2f2',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  backgroundColor: page === 1 ? '#f3f4f6' : 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  color: page === 1 ? '#d1d5db' : '#374151'
                }}
              >
                <ChevronLeft size={18} />
              </button>

              <span style={{ fontSize: '14px', color: '#6b7280' }}>
                Page {page} sur {totalPages}
              </span>

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  backgroundColor: page === totalPages ? '#f3f4f6' : 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer',
                  color: page === totalPages ? '#d1d5db' : '#374151'
                }}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal d'édition */}
      <NotesModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingNote(null); }}
        onSave={handleSaveNote}
        editingNote={editingNote}
        currentContext={contexteDeSaisie}
        clients={clients}
        sites={sites}
        accounts={accounts}
        token={token}
        onOuvrirVue={onOuvrirVue}
      />

      {/* Modal de confirmation de suppression */}
      {deletingNote && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setDeletingNote(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                backgroundColor: '#fef2f2',
                borderRadius: '12px'
              }}>
                <Trash2 size={24} style={{ color: '#dc2626' }} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>
                  Supprimer la note ?
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
                  Cette action est irreversible.
                </p>
              </div>
            </div>

            <div style={{
              padding: '12px 16px',
              backgroundColor: '#FAF9F5',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '14px',
              color: '#374151'
            }}>
              "{deletingNote.content.length > 100
                ? deletingNote.content.substring(0, 100) + '...'
                : deletingNote.content}"
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeletingNote(null)}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#374151',
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleting ? '#f87171' : '#dc2626',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'white',
                  cursor: deleting ? 'not-allowed' : 'pointer'
                }}
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles pour l'animation du spinner */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default NotesArchive;
